/**
 * cascadeTracerService.ts
 *
 * READ-ONLY tracer for OKR Tracker's cascade feature. Cascade assignment
 * itself is owned by the OKR Tracker app (`okr.rascalrepublic.com`); EPR
 * just renders an audit timeline of what's been cascaded to a given
 * person so HR/manager/employee can see context during review.
 *
 * Source collection: `cascadedTasks` on the shared `okr-tracker-staging`
 * Firestore. Rule allows `read: if isSignedIn()`.
 *
 * Two query paths:
 *   1. By UID  — preferred when the recipient already has an OKR Tracker
 *      account (cascade may have been issued to assigneeType='user').
 *   2. By email — fallback for cascades issued to external recipients
 *      (assigneeType='external') who haven't signed in yet.
 *
 * Both paths are merged + de-duped by id. We also enrich each task with
 * its parent KR's krNumber/title and parent objective title so EPR can
 * render a meaningful card without an OKR Tracker round-trip on the
 * recipient side.
 */

import {
  collection, query, where, getDocs, doc, getDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { resolveOwnerIdByEmail } from './okrTrackerSyncService';

// ─── Source-of-truth shapes (mirror OKR Tracker types) ─────────────

export type CascadeStatus = 'Pending' | 'In Progress' | 'Done' | 'Blocked';

interface CascadedTaskDoc {
  parentKrId: string;
  parentObjectiveId: string;
  assigneeType: 'user' | 'external';
  assigneeUid?: string;
  assigneeEmail: string;
  assigneeName: string;
  assignedByUid: string;
  cycle: string;             // year as string, e.g. "2026"
  quarter?: string;          // "Q1 2026" etc.
  title: string;
  description?: string;
  status: CascadeStatus;
  progress: number;          // 0..100
  dueDate?: string;          // ISO date
  inviteAcceptedAt?: string; // ISO timestamp (external only)
  createdAt: string;         // ISO
  updatedAt: string;         // ISO
}

interface KeyResultLite {
  krNumber?: string;
  title: string;
}

interface ObjectiveLite {
  title?: string;
}

// ─── Outward shape ─────────────────────────────────────────────────

export interface CascadeTracerEntry {
  /** cascadedTasks doc id. */
  id: string;

  // Parent KR / objective enrichment
  parentKrId: string;
  parentObjectiveId: string;
  krNumber?: string;
  krTitle?: string;
  objectiveTitle?: string;

  // Recipient identity
  assigneeName: string;
  assigneeEmail: string;
  assigneeUid?: string;

  // Issuer (UID; resolution to a human name is the caller's job)
  assignedByUid: string;

  // The task itself
  title: string;
  description?: string;
  status: CascadeStatus;
  progress: number;
  cycle: string;
  quarter?: string;

  // Timeline anchors (ISO strings — keep raw for caller-side formatting)
  createdAt: string;
  inviteAcceptedAt?: string;
  updatedAt: string;
  dueDate?: string;
}

export interface FetchCascadeForEmployeeResult {
  entries: CascadeTracerEntry[];
  /** Resolved OKR Tracker UID via email lookup, or null if not found. */
  ownerId: string | null;
  /** Count summary by status (for HR overview badges). */
  counts: Record<CascadeStatus, number>;
}

// ─── Fetch ─────────────────────────────────────────────────────────

export interface FetchCascadeForEmployeeInput {
  email: string;
  /** Optional cycle-year filter. When omitted, returns every cycle. */
  year?: number;
}

/**
 * Fetch the cascade timeline for one employee. Looks up the recipient's
 * UID by email (so cascades issued via assigneeUid are found) AND queries
 * by assigneeEmail (so cascades issued to externals before they signed
 * in are also found). De-dupes on doc id.
 *
 * Returns enriched entries with parent KR + objective title pulled in
 * a single round-trip per unique KR/objective (cached within the call).
 */
export async function fetchCascadeForEmployee(
  input: FetchCascadeForEmployeeInput,
): Promise<FetchCascadeForEmployeeResult> {
  const trimmedEmail = input.email.trim().toLowerCase();
  if (!trimmedEmail) {
    return { entries: [], ownerId: null, counts: emptyCounts() };
  }

  const ownerId = await resolveOwnerIdByEmail(input.email);

  // Two queries, merged. Both may return overlapping docs.
  const byEmailQ = query(
    collection(db, 'cascadedTasks'),
    where('assigneeEmail', '==', trimmedEmail),
  );
  const queries: Promise<{ id: string; data: CascadedTaskDoc }[]>[] = [
    getDocs(byEmailQ).then(s => s.docs.map(d => ({ id: d.id, data: d.data() as CascadedTaskDoc }))),
  ];

  if (ownerId) {
    const byUidQ = query(
      collection(db, 'cascadedTasks'),
      where('assigneeUid', '==', ownerId),
    );
    queries.push(getDocs(byUidQ).then(s => s.docs.map(d => ({ id: d.id, data: d.data() as CascadedTaskDoc }))));
  }

  const results = (await Promise.all(queries)).flat();

  // De-dupe by id
  const seen = new Map<string, CascadedTaskDoc>();
  for (const r of results) {
    if (!seen.has(r.id)) seen.set(r.id, r.data);
  }

  // Optional year filter (cycle is stored as string)
  const yearStr = input.year != null ? String(input.year) : null;
  const filtered = Array.from(seen.entries()).filter(
    ([, data]) => yearStr == null || data.cycle === yearStr,
  );

  // Enrich each entry with parent KR + objective titles
  const krCache = new Map<string, KeyResultLite | null>();
  const objCache = new Map<string, ObjectiveLite | null>();

  const enrich = async (id: string, data: CascadedTaskDoc): Promise<CascadeTracerEntry> => {
    const kr = await resolveCached(krCache, data.parentKrId, async () => {
      const snap = await getDoc(doc(db, 'keyResults', data.parentKrId));
      return snap.exists() ? (snap.data() as KeyResultLite) : null;
    });
    const obj = await resolveCached(objCache, data.parentObjectiveId, async () => {
      const snap = await getDoc(doc(db, 'objectives', data.parentObjectiveId));
      return snap.exists() ? (snap.data() as ObjectiveLite) : null;
    });

    return {
      id,
      parentKrId:        data.parentKrId,
      parentObjectiveId: data.parentObjectiveId,
      krNumber:          kr?.krNumber,
      krTitle:           kr?.title,
      objectiveTitle:    obj?.title,
      assigneeName:      data.assigneeName,
      assigneeEmail:     data.assigneeEmail,
      assigneeUid:       data.assigneeUid,
      assignedByUid:     data.assignedByUid,
      title:             data.title,
      description:       data.description,
      status:            data.status,
      progress:          data.progress,
      cycle:             data.cycle,
      quarter:           data.quarter,
      createdAt:         data.createdAt,
      inviteAcceptedAt:  data.inviteAcceptedAt,
      updatedAt:         data.updatedAt,
      dueDate:           data.dueDate,
    };
  };

  const entries = await Promise.all(filtered.map(([id, data]) => enrich(id, data)));

  // Sort newest first (createdAt desc)
  entries.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));

  const counts = entries.reduce<Record<CascadeStatus, number>>(
    (acc, e) => { acc[e.status] = (acc[e.status] ?? 0) + 1; return acc; },
    emptyCounts(),
  );

  return { entries, ownerId, counts };
}

// ─── Helpers ───────────────────────────────────────────────────────

async function resolveCached<T>(
  cache: Map<string, T | null>,
  key: string,
  fetcher: () => Promise<T | null>,
): Promise<T | null> {
  if (cache.has(key)) return cache.get(key) ?? null;
  const v = await fetcher();
  cache.set(key, v);
  return v;
}

function emptyCounts(): Record<CascadeStatus, number> {
  return { 'Pending': 0, 'In Progress': 0, 'Done': 0, 'Blocked': 0 };
}

/**
 * Compute a 3-event timeline from the snapshot timestamps the OKR
 * Tracker stores. OKR Tracker does not maintain a full event log on
 * the cascade doc — it stores `createdAt`, `inviteAcceptedAt` (external
 * only), and `updatedAt`. We surface them as discrete events so the EPR
 * UI can render a vertical timeline.
 *
 * Caller decides how to render. We just compute structure.
 */
export interface TimelineEvent {
  ts: string;
  label: string;
  kind: 'assigned' | 'accepted' | 'updated';
}

export function deriveTimeline(entry: CascadeTracerEntry): TimelineEvent[] {
  const events: TimelineEvent[] = [
    { ts: entry.createdAt, label: 'Cascaded', kind: 'assigned' },
  ];
  if (entry.inviteAcceptedAt) {
    events.push({
      ts:    entry.inviteAcceptedAt,
      label: 'Magic-link accepted',
      kind:  'accepted',
    });
  }
  if (entry.updatedAt && entry.updatedAt !== entry.createdAt) {
    events.push({
      ts:    entry.updatedAt,
      label: `Status: ${entry.status} (${entry.progress}%)`,
      kind:  'updated',
    });
  }
  // Chronological asc
  events.sort((a, b) => (a.ts < b.ts ? -1 : 1));
  return events;
}
